import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Search, QrCode, ShieldCheck, ExternalLink, X } from "lucide-react";
import { toast } from "sonner";
import QRScanner from "@/components/QRScanner";

const MODES = [
  { key: "ethic_id", label: "EthicCred ID", placeholder: "EC-XXXX-XXXX" },
  { key: "email", label: "Email", placeholder: "name@domain.com" },
  { key: "phone", label: "Phone", placeholder: "+91 98123 45678" },
  { key: "pan", label: "PAN (encrypted)", placeholder: "ABCDE1234F" },
];

/**
 * Search-before-create card. Renders 4 search modes + QR scan.
 * Calls /api/professionals/lookup. When `onFound` is invoked the parent
 * gets the full professional object (with score/tier/passport_url/etc).
 */
export default function ProfessionalSearch({ onFound, onCancel, autoFocus = true, allowSkip = true }) {
  const [mode, setMode] = useState("ethic_id");
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const lookup = async (override) => {
    const v = (override?.value ?? value).trim();
    const m = override?.mode ?? mode;
    if (!v) { toast.error("Enter a value to search."); return; }
    setLoading(true); setNotFound(false);
    try {
      const { data } = await api.post("/professionals/lookup", { mode: m, value: v });
      if (data.found) {
        onFound(data.professional);
      } else {
        setNotFound(true);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleQR = (decoded) => {
    setShowQR(false);
    // Try to extract EthicCred ID from URL like /verify/EC-XXXX-XXXX or /passport/EC-XXXX-XXXX
    const m = decoded.match(/(EC-[A-Z0-9-]+)/i);
    const ethicId = m ? m[1].toUpperCase() : decoded.trim().toUpperCase();
    setMode("ethic_id");
    setValue(ethicId);
    lookup({ mode: "ethic_id", value: ethicId });
  };

  const submit = (e) => { e.preventDefault(); lookup(); };

  return (
    <Card className="rounded-sm border-ec-line" data-testid="professional-search">
      <CardContent className="p-5 sm:p-6">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-sm bg-ec-emerald/10 text-ec-emerald flex items-center justify-center">
            <ShieldCheck className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <div className="eyebrow text-ec-emerald">Step 1 of 2</div>
            <h2 className="font-display text-xl sm:text-2xl font-black tracking-tight">Search existing professional</h2>
            <p className="text-xs sm:text-sm text-ec-ink-soft mt-1">If the candidate already holds an EthicCred passport we'll link them instead of creating a duplicate.</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-1 mb-3" data-testid="search-modes">
          {MODES.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => { setMode(m.key); setNotFound(false); }}
              className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${mode === m.key ? "border-ec-ink bg-ec-ink text-white" : "border-ec-line text-ec-ink-soft hover:bg-ec-bg-mute"}`}
              data-testid={`search-mode-${m.key}`}
            >
              {m.label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="flex flex-wrap gap-2" data-testid="search-form">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ec-ink-soft" />
            <Input
              value={value}
              onChange={(e) => { setValue(e.target.value); setNotFound(false); }}
              placeholder={MODES.find((m) => m.key === mode)?.placeholder}
              className="rounded-sm pl-9 h-11"
              autoFocus={autoFocus}
              data-testid="search-input"
            />
          </div>
          <Button type="submit" disabled={loading} className="rounded-sm h-11 bg-ec-ink hover:bg-zinc-800 gap-2" data-testid="search-submit">
            <Search className="w-4 h-4" /> {loading ? "Searching…" : "Search"}
          </Button>
          <Button type="button" variant="outline" className="rounded-sm h-11 gap-2" onClick={() => setShowQR(true)} data-testid="search-scan-qr">
            <QrCode className="w-4 h-4" /> Scan QR
          </Button>
          {allowSkip && (
            <Button type="button" variant="ghost" className="rounded-sm h-11 text-ec-ink-soft hover:text-ec-ink" onClick={onCancel} data-testid="search-skip">
              Skip — create new record
            </Button>
          )}
        </form>

        {notFound && (
          <div className="mt-4 p-4 border border-amber-200 bg-amber-50 rounded-sm flex items-start gap-3" data-testid="search-not-found">
            <X className="w-4 h-4 text-amber-700 mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold text-amber-900">No EthicCred passport found.</div>
              <div className="text-amber-800 mt-1">You can still create a record below — the individual will be invited to claim it once they sign up.</div>
              {allowSkip && (
                <button type="button" onClick={onCancel} className="mt-2 underline text-amber-900 text-xs" data-testid="search-create-anyway">Create new record →</button>
              )}
            </div>
          </div>
        )}
      </CardContent>

      {showQR && <QRScanner onScan={handleQR} onClose={() => setShowQR(false)} />}
    </Card>
  );
}

/**
 * Profile card shown after a successful lookup. Exposes "Add as Employee"
 * (or generic primary action), "View Passport", and "Cancel" buttons.
 */
export function ProfessionalFoundCard({ pro, onPrimary, primaryLabel = "Add as Employee", onCancel, primaryTestId = "found-primary" }) {
  const initials = (pro.full_name || pro.email || "").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  return (
    <Card className="rounded-sm border-ec-emerald/30 bg-ec-emerald/5" data-testid="professional-found-card">
      <CardContent className="p-5 sm:p-6">
        <div className="flex flex-wrap gap-4 items-start">
          {pro.avatar_url ? (
            <img src={pro.avatar_url} alt={pro.full_name} className="w-16 h-16 rounded-sm object-cover border border-ec-line" />
          ) : (
            <div className="w-16 h-16 rounded-sm bg-ec-ink text-white flex items-center justify-center font-display text-xl font-black">{initials || "EC"}</div>
          )}
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-display text-xl font-black">{pro.full_name || "Unnamed Professional"}</h3>
              {pro.verified_email && <ShieldCheck className="w-4 h-4 text-ec-emerald" />}
            </div>
            <div className="text-xs font-mono-plex text-ec-ink-soft mt-0.5">{pro.ethic_id || "— no EthicCred ID —"}</div>
            <div className="text-sm text-ec-ink-soft mt-1">{pro.headline || pro.email}</div>
            <div className="flex flex-wrap gap-2 mt-3">
              <Pill label="Trust Tier" value={pro.trust_tier} testid="found-tier" />
              <Pill label="Score" value={`${pro.credibility_score} / ${pro.max_score}`} testid="found-score" />
              {pro.location && <Pill label="Location" value={pro.location} />}
            </div>
            {pro.current_employment && (
              <div className="mt-3 text-xs text-ec-ink-soft border-l-2 border-ec-emerald pl-3" data-testid="found-current-employment">
                Currently: <span className="text-ec-ink font-semibold">{pro.current_employment.designation}</span>
                {pro.current_employment.department ? ` · ${pro.current_employment.department}` : ""}
                {pro.current_employment.issuer_name ? ` @ ${pro.current_employment.issuer_name}` : ""}
                {pro.current_employment.employment_status ? ` (${pro.current_employment.employment_status})` : ""}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap justify-end gap-2 mt-5 pt-4 border-t border-ec-emerald/20">
          {pro.ethic_id && (
            <a href={pro.passport_url || `/passport/${pro.ethic_id}`} target="_blank" rel="noreferrer">
              <Button type="button" variant="outline" className="rounded-sm gap-2" data-testid="found-view-passport">
                <ExternalLink className="w-4 h-4" /> View Passport
              </Button>
            </a>
          )}
          <Button type="button" variant="outline" className="rounded-sm" onClick={onCancel} data-testid="found-cancel">Cancel</Button>
          <Button type="button" onClick={() => onPrimary(pro)} className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink gap-2" data-testid={primaryTestId}>
            <ShieldCheck className="w-4 h-4" /> {primaryLabel}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Pill({ label, value, testid }) {
  return (
    <div className="px-2.5 py-1 rounded-sm bg-white border border-ec-line text-xs" data-testid={testid}>
      <span className="text-ec-ink-soft">{label}: </span><span className="font-semibold text-ec-ink">{value}</span>
    </div>
  );
}
